import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem
} from '@mui/material';
import {
  People as PeopleIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  AccessTime as TimeIcon,
  Person as PersonIcon,
  ExpandMore as ExpandMoreIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  VideoLibrary as VideoLibraryIcon,
  Bookmark as BookmarkIcon,
  Logout as LogoutIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { isAdminUser } from '../config/adminConfig';

interface UserData {
  id: string;
  name: string;
  email: string;
  status: 'online' | 'offline';
  lastLogin: string;
  stayDuration: string;
  videoCount: number;
  checkpointCount: number;
  loginTime: string;
  isRegistered: boolean;
}

interface VBidsData {
  id: string;
  videoId: string;
  videoTitle: string;
  videoUrl: string;
  vbids: Array<{
    time: number;
    note: string;
    id: string;
  }>;
  checkpointCount: number;
  createdAt: string;
  updatedAt: string;
}

interface EditFormData {
  name: string;
  email: string;
  status: 'online' | 'offline';
}

const UserManagementWindow: React.FC = () => {
  const { user } = useAuth();
  const [userData, setUserData] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUserForDelete, setSelectedUserForDelete] = useState<UserData | null>(null);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const [selectedUserForLogout, setSelectedUserForLogout] = useState<UserData | null>(null);
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' as 'success' | 'error' 
  });

  // 編輯相關狀態
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedUserForEdit, setSelectedUserForEdit] = useState<UserData | null>(null);
  const [editForm, setEditForm] = useState<EditFormData>({
    name: '',
    email: '',
    status: 'offline'
  });
  const [userVBids, setUserVBids] = useState<VBidsData[]>([]);
  const [loadingVBids, setLoadingVBids] = useState(false);

  // 檢查是否為管理員
  const isAdmin = isAdminUser(user?.email, user?.name);

  // 獨立的用戶管理數據獲取函數（完全獨立，不受任何干擾）
  const fetchUserManagementData = async () => {
    try {
      console.log('🔍 獨立用戶管理視窗：獲取用戶數據...');
      setLoading(true);
      setError(null);
      
      const { getDb, waitForFirebase } = await import('../services/firebase');
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }

      const { collection, getDocs, query, orderBy } = await import('firebase/firestore');
      
      // 獲取所有用戶數據
      const usersSnapshot = await getDocs(query(collection(db, 'users'), orderBy('createdAt', 'desc')));
      const userWatchHistorySnapshot = await getDocs(collection(db, 'userWatchHistory'));
      const videosSnapshot = await getDocs(collection(db, 'sharedVbids'));
      
      // 收集所有唯一用戶
      const allUsersMap = new Map<string, any>();
      
      // 從 users 集合添加正式註冊用戶
      usersSnapshot.forEach(doc => {
        const data = doc.data();
        allUsersMap.set(doc.id, {
          id: doc.id,
          name: data.displayName || data.name || '未設定',
          email: data.email || '',
          status: data.isOnline ? 'online' : 'offline',
          lastLogin: data.lastLoginAt ? 
            (data.lastLoginAt.toDate ? data.lastLoginAt.toDate().toISOString() : new Date(data.lastLoginAt).toISOString()) : 
            (data.updatedAt ? 
              (data.updatedAt.toDate ? data.updatedAt.toDate().toISOString() : new Date(data.updatedAt).toISOString()) : 
              (data.createdAt ? 
                (data.createdAt.toDate ? data.createdAt.toDate().toISOString() : new Date(data.createdAt).toISOString()) : 
                '')),
          loginTime: data.lastLoginAt ? 
            (data.lastLoginAt.toDate ? data.lastLoginAt.toDate().toISOString() : new Date(data.lastLoginAt).toISOString()) : 
            (data.updatedAt ? 
              (data.updatedAt.toDate ? data.updatedAt.toDate().toISOString() : new Date(data.updatedAt).toISOString()) : 
              (data.createdAt ? 
                (data.createdAt.toDate ? data.createdAt.toDate().toISOString() : new Date(data.createdAt).toISOString()) : 
                '')),
          isRegistered: true
        });
      });
      
      // 從 userWatchHistory 集合添加所有用戶，但優先保留 users 集合中的完整信息
      userWatchHistorySnapshot.forEach(doc => {
        const data = doc.data();
        const userId = data.userId;
        if (userId) {
          // 🔧 修復：檢查是否在 users 集合中有對應的完整信息
          let userInfo = null;
          let existingUserKey = null;
          
          // 首先檢查是否已經存在相同的用戶（通過 email 匹配）
          for (const [key, existingUser] of allUsersMap) {
            if (existingUser.email === userId || 
                (userId.includes('@') && existingUser.email === userId) ||
                (existingUser.email && existingUser.email === userId)) {
              userInfo = existingUser;
              existingUserKey = key;
              break;
            }
          }
          
          // 如果沒找到，再檢查 users 集合
          if (!userInfo) {
            usersSnapshot.forEach(userDoc => {
              const userData = userDoc.data();
              // 檢查多種匹配方式：ID 匹配、userId 字段匹配、email 匹配
              if (userDoc.id === userId || 
                  userData.userId === userId || 
                  userData.email === userId ||
                  (userId.includes('@') && userData.email === userId)) {
                userInfo = {
                  id: userDoc.id, // 使用 users 集合的 ID (Firebase UID)
                  name: userData.displayName || userData.name || '未設定',
                  email: userData.email || '',
                  status: userData.isOnline ? 'online' : 'offline',
                  lastLogin: userData.lastLoginAt ? 
                    (userData.lastLoginAt.toDate ? userData.lastLoginAt.toDate().toISOString() : new Date(userData.lastLoginAt).toISOString()) : 
                    '',
                  loginTime: userData.lastLoginAt ? 
                    (userData.lastLoginAt.toDate ? userData.lastLoginAt.toDate().toISOString() : new Date(userData.lastLoginAt).toISOString()) : 
                    '',
                  isRegistered: true
                };
                existingUserKey = userDoc.id;
              }
            });
          }
          
          // 如果沒有在 users 集合中找到，則創建基本用戶信息
          if (!userInfo) {
            let userName = '未設定';
            if (userId.includes('@')) {
              userName = userId.split('@')[0];
            } else if (userId.startsWith('guest_')) {
              userName = '訪客用戶';
            } else {
              userName = userId.substring(0, 8) + '...';
            }
            
            userInfo = {
              id: userId,
              name: userName,
              email: userId.includes('@') ? userId : '',
              status: 'offline',
              lastLogin: data.lastOpenDate ? 
                (data.lastOpenDate.toDate ? data.lastOpenDate.toDate().toISOString() : new Date(data.lastOpenDate).toISOString()) : 
                '',
              loginTime: data.lastOpenDate ? 
                (data.lastOpenDate.toDate ? data.lastOpenDate.toDate().toISOString() : new Date(data.lastOpenDate).toISOString()) : 
                '',
              isRegistered: false
            };
            allUsersMap.set(userId, userInfo);
          } else if (existingUserKey) {
            // 如果找到了現有用戶，更新其最後登入時間（如果 userWatchHistory 的時間更晚）
            const watchHistoryLastLogin = data.lastOpenDate ? 
              (data.lastOpenDate.toDate ? data.lastOpenDate.toDate().toISOString() : new Date(data.lastOpenDate).toISOString()) : 
              '';
            if (watchHistoryLastLogin && (!userInfo.lastLogin || watchHistoryLastLogin > userInfo.lastLogin)) {
              userInfo.lastLogin = watchHistoryLastLogin;
              userInfo.loginTime = watchHistoryLastLogin;
            }
          }
        }
      });
      
      // 計算每個用戶的影片和重點數量
      const userStatsData: UserData[] = [];
      for (const [userId, userData] of allUsersMap) {
        let userVideoCount = 0;
        let userCheckpointCount = 0;
        videosSnapshot.forEach(videoDoc => {
          const videoData = videoDoc.data();
          // 🔧 修復：支持多種 userId 匹配方式
          if (videoData.userId === userId || 
              videoData.userId === userData.email ||
              (userData.email && videoData.userId === userData.email)) {
            userVideoCount++;
            userCheckpointCount += videoData.checkpointCount || 0;
          }
        });
        
        userStatsData.push({
          id: userData.id,
          name: userData.name,
          email: userData.email,
          status: userData.status,
          lastLogin: userData.lastLogin,
          stayDuration: '00:00',
          videoCount: userVideoCount,
          checkpointCount: userCheckpointCount,
          loginTime: userData.loginTime,
          isRegistered: userData.isRegistered
        });
      }
      
      // 🔧 修復：按用戶類型分組排序
      // 第一群：有email的註冊用戶（按最後登入時間降序）
      // 第二群：訪客用戶（按最後登入時間降序）
      const sortedUserData = userStatsData.sort((a, b) => {
        // 首先按是否註冊分組（註冊用戶在前）
        if (a.isRegistered !== b.isRegistered) {
          return a.isRegistered ? -1 : 1;
        }
        
        // 同組內按最後登入時間降序排序
        const aTime = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
        const bTime = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
        return bTime - aTime;
      });
      
      setUserData(sortedUserData);
      console.log(`✅ 獨立用戶管理視窗：數據獲取完成，共 ${sortedUserData.length} 個用戶`);
      console.log(`📊 註冊用戶: ${sortedUserData.filter(u => u.isRegistered).length} 個`);
      console.log(`📊 訪客用戶: ${sortedUserData.filter(u => !u.isRegistered).length} 個`);
      
    } catch (error) {
      console.error('❌ 獨立用戶管理視窗：獲取數據失敗:', error);
      setError('獲取用戶數據失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  };

  // 格式化最後登入時間
  const formatLastLogin = (lastLogin: string): string => {
    if (!lastLogin) return '從未登入';
    
    try {
      const date = new Date(lastLogin);
      if (isNaN(date.getTime())) return '無效日期';
      
      return date.toLocaleString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return '格式錯誤';
    }
  };

  // 處理編輯用戶
  const handleEditUser = async (userToEdit: UserData) => {
    // 檢查是否為管理員
    if (!isAdmin) {
      setSnackbar({ 
        open: true, 
        message: '您沒有權限編輯用戶', 
        severity: 'error' 
      });
      return;
    }

    setSelectedUserForEdit(userToEdit);
    setEditForm({
      name: userToEdit.name,
      email: userToEdit.email,
      status: userToEdit.status
    });

    // 載入用戶的 VBIDs 數據
    setLoadingVBids(true);
    try {
      await fetchUserVBids(userToEdit.id);
    } catch (error) {
      console.error('載入用戶 VBIDs 失敗:', error);
      setSnackbar({ 
        open: true, 
        message: '載入用戶數據失敗', 
        severity: 'error' 
      });
    } finally {
      setLoadingVBids(false);
    }

    setEditDialogOpen(true);
  };

  // 獲取用戶的 VBIDs 數據
  const fetchUserVBids = async (userId: string) => {
    try {
      const { getDb, waitForFirebase } = await import('../services/firebase');
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }

      const { collection, getDocs, query, where } = await import('firebase/firestore');
      
      // 先只查詢 userId，避免複合索引問題
      const vbidsSnapshot = await getDocs(
        query(
          collection(db, 'sharedVbids'),
          where('userId', '==', userId)
        )
      );

      const vbidsData: VBidsData[] = [];
      vbidsSnapshot.forEach(doc => {
        const data = doc.data();
        vbidsData.push({
          id: doc.id,
          videoId: data.videoId || '',
          videoTitle: data.videoTitle || data.title || '無標題',
          videoUrl: data.videoUrl || '',
          vbids: data.vbids || [],
          checkpointCount: data.checkpointCount || data.vbids?.length || 0,
          createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt || '',
          updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt || ''
        });
      });

      // 客戶端排序：按創建時間降序
      vbidsData.sort((a, b) => {
        const aTime = new Date(a.createdAt).getTime();
        const bTime = new Date(b.createdAt).getTime();
        return bTime - aTime;
      });

      setUserVBids(vbidsData);
      console.log(`✅ 載入用戶 ${userId} 的 VBIDs 數據:`, vbidsData.length, '條記錄');
    } catch (error) {
      console.error('❌ 載入用戶 VBIDs 失敗:', error);
      throw error;
    }
  };

  // 保存編輯
  const handleSaveEdit = async () => {
    if (!selectedUserForEdit || !isAdmin) return;

    try {
      const { getDb, waitForFirebase } = await import('../services/firebase');
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }

      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');

      // 更新用戶基本信息
      const userRef = doc(db, 'users', selectedUserForEdit.id);
      await updateDoc(userRef, {
        name: editForm.name,
        displayName: editForm.name,
        email: editForm.email,
        isOnline: editForm.status === 'online',
        updatedAt: serverTimestamp()
      });

      setSnackbar({ 
        open: true, 
        message: `用戶 "${editForm.name}" 已成功更新`, 
        severity: 'success' 
      });
      
      setEditDialogOpen(false);
      setSelectedUserForEdit(null);
      
      // 重新載入數據
      await fetchUserManagementData();
      
    } catch (error) {
      console.error('❌ 更新用戶失敗:', error);
      setSnackbar({ 
        open: true, 
        message: '更新用戶失敗，請稍後再試', 
        severity: 'error' 
      });
    }
  };

  // 更新 VBID 記錄
  const handleUpdateVBid = async (vbidId: string, field: string, value: any) => {
    if (!isAdmin) return;

    try {
      const { getDb, waitForFirebase } = await import('../services/firebase');
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }

      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');

      const vbidRef = doc(db, 'sharedVbids', vbidId);
      const updateData: any = {
        updatedAt: serverTimestamp()
      };

      if (field === 'videoTitle') {
        updateData.videoTitle = value;
        updateData.title = value;
      } else if (field === 'videoUrl') {
        updateData.videoUrl = value;
      } else if (field.startsWith('vbid_')) {
        const vbidIndex = parseInt(field.split('_')[1]);
        const vbids = [...userVBids.find(v => v.id === vbidId)?.vbids || []];
        if (vbids[vbidIndex]) {
          if (field.includes('time')) {
            vbids[vbidIndex].time = parseFloat(value) || 0;
          } else if (field.includes('note')) {
            vbids[vbidIndex].note = value;
          }
          updateData.vbids = vbids;
          updateData.checkpointCount = vbids.length;
        }
      }

      await updateDoc(vbidRef, updateData);

      // 更新本地狀態
      setUserVBids(prev => prev.map(vbid => 
        vbid.id === vbidId 
          ? { ...vbid, ...updateData }
          : vbid
      ));

      setSnackbar({ 
        open: true, 
        message: 'VBID 記錄已更新', 
        severity: 'success' 
      });
      
    } catch (error) {
      console.error('❌ 更新 VBID 失敗:', error);
      setSnackbar({ 
        open: true, 
        message: '更新 VBID 失敗，請稍後再試', 
        severity: 'error' 
      });
    }
  };

  // 處理刪除用戶
  const handleDeleteUser = (userToDelete: UserData) => {
    // 檢查是否為管理員
    if (!isAdmin) {
      setSnackbar({ 
        open: true, 
        message: '您沒有權限刪除用戶', 
        severity: 'error' 
      });
      return;
    }

    // 防止刪除自己
    if (userToDelete.email === user?.email) {
      setSnackbar({ 
        open: true, 
        message: '不能刪除自己的帳號', 
        severity: 'error' 
      });
      return;
    }

    setSelectedUserForDelete(userToDelete);
    setDeleteDialogOpen(true);
  };

  // 處理強制登出用戶
  const handleForceLogout = (userToLogout: UserData) => {
    // 檢查是否為管理員
    if (!isAdmin) {
      setSnackbar({ 
        open: true, 
        message: '您沒有權限強制登出用戶', 
        severity: 'error' 
      });
      return;
    }

    // 防止強制登出自己
    if (userToLogout.email === user?.email) {
      setSnackbar({ 
        open: true, 
        message: '不能強制登出自己', 
        severity: 'error' 
      });
      return;
    }

    setSelectedUserForLogout(userToLogout);
    setLogoutDialogOpen(true);
  };

  // 確認強制登出
  const handleConfirmLogout = async () => {
    if (!selectedUserForLogout || !isAdmin) return;

    try {
      const { getDb, waitForFirebase } = await import('../services/firebase');
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }

      const { doc, updateDoc, serverTimestamp } = await import('firebase/firestore');

      // 更新用戶狀態為離線
      const userRef = doc(db, 'users', selectedUserForLogout.id);
      await updateDoc(userRef, {
        isOnline: false,
        lastLogoutAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      setSnackbar({ 
        open: true, 
        message: `用戶 "${selectedUserForLogout.name}" 已強制登出`, 
        severity: 'success' 
      });
      
      setLogoutDialogOpen(false);
      setSelectedUserForLogout(null);
      
      // 重新載入數據
      await fetchUserManagementData();
      
    } catch (error) {
      console.error('❌ 強制登出失敗:', error);
      setSnackbar({ 
        open: true, 
        message: '強制登出失敗，請稍後再試', 
        severity: 'error' 
      });
    }
  };

  // 確認刪除用戶
  const handleConfirmDelete = async () => {
    if (!selectedUserForDelete || !isAdmin) return;

    try {
      console.log('🗑️ 開始刪除用戶:', selectedUserForDelete.id);
      
      const { getDb, waitForFirebase } = await import('../services/firebase');
      await waitForFirebase();
      const db = getDb();
      if (!db) {
        throw new Error('Firestore 未初始化');
      }

      const { doc, deleteDoc, collection, query, where, getDocs } = await import('firebase/firestore');

      // 1. 刪除 users 集合中的用戶記錄
      const userRef = doc(db, 'users', selectedUserForDelete.id);
      await deleteDoc(userRef);
      console.log('✅ 用戶記錄已刪除');

      // 2. 刪除 userWatchHistory 中的記錄
      const watchHistoryQuery = query(
        collection(db, 'userWatchHistory'),
        where('userId', '==', selectedUserForDelete.id)
      );
      const watchHistorySnapshot = await getDocs(watchHistoryQuery);
      const deleteWatchHistoryPromises = watchHistorySnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deleteWatchHistoryPromises);
      console.log(`✅ 已刪除 ${watchHistorySnapshot.size} 條觀看記錄`);

      // 3. 刪除 sharedVbids 中的記錄
      const sharedVbidsQuery = query(
        collection(db, 'sharedVbids'),
        where('userId', '==', selectedUserForDelete.id)
      );
      const sharedVbidsSnapshot = await getDocs(sharedVbidsQuery);
      const deleteSharedVbidsPromises = sharedVbidsSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deleteSharedVbidsPromises);
      console.log(`✅ 已刪除 ${sharedVbidsSnapshot.size} 條影片記錄`);

      // 4. 刪除 userNotes 中的記錄
      const userNotesQuery = query(
        collection(db, 'userNotes'),
        where('userId', '==', selectedUserForDelete.id)
      );
      const userNotesSnapshot = await getDocs(userNotesQuery);
      const deleteUserNotesPromises = userNotesSnapshot.docs.map(doc => deleteDoc(doc.ref));
      await Promise.all(deleteUserNotesPromises);
      console.log(`✅ 已刪除 ${userNotesSnapshot.size} 條筆記記錄`);

      setSnackbar({ 
        open: true, 
        message: `用戶 "${selectedUserForDelete.name}" 已成功刪除`, 
        severity: 'success' 
      });
      
      setDeleteDialogOpen(false);
      setSelectedUserForDelete(null);
      
      // 重新載入數據
      await fetchUserManagementData();
      
    } catch (error) {
      console.error('❌ 刪除用戶失敗:', error);
      setSnackbar({ 
        open: true, 
        message: '刪除用戶失敗，請稍後再試', 
        severity: 'error' 
      });
    }
  };

  // 關閉視窗
  const closeWindow = () => {
    window.close();
  };

  // 初始化數據
  useEffect(() => {
    fetchUserManagementData();
  }, []);

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress />
        <Typography variant="body1">正在載入用戶數據...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={fetchUserManagementData}>
          重試
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* 標題欄 */}
      <Box sx={{ 
        p: 2, 
        borderBottom: 1, 
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        backgroundColor: '#f5f5f5'
      }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PeopleIcon />
          用戶管理 - 獨立視窗
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="刷新數據">
            <IconButton onClick={fetchUserManagementData}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="關閉視窗">
            <IconButton onClick={closeWindow} color="error">
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      
      {/* 統計信息 */}
      <Box sx={{ p: 2, backgroundColor: '#fafafa', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" color="primary">
          系統統計
        </Typography>
        <Box sx={{ display: 'flex', gap: 4, mt: 1 }}>
          <Typography variant="body1">
            <strong>總用戶數:</strong> {userData.length}
          </Typography>
          <Typography variant="body1">
            <strong>在線用戶:</strong> {userData.filter(u => u.status === 'online').length}
          </Typography>
          <Typography variant="body1">
            <strong>離線用戶:</strong> {userData.filter(u => u.status === 'offline').length}
          </Typography>
          <Typography variant="body1">
            <strong>註冊用戶:</strong> {userData.filter(u => u.isRegistered).length}
          </Typography>
        </Box>
      </Box>
      
      {/* 用戶列表 */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <TableContainer sx={{ height: '100%' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>用戶名稱</TableCell>
                <TableCell>郵箱</TableCell>
                <TableCell>狀態</TableCell>
                <TableCell>最後登入</TableCell>
                <TableCell>影片數量</TableCell>
                <TableCell>重點數量</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {userData.map((user, index) => {
                // 🔧 新增：檢查是否需要添加分組標題
                const isFirstRegistered = user.isRegistered && 
                  (index === 0 || !userData[index - 1].isRegistered);
                const isFirstGuest = !user.isRegistered && 
                  (index === 0 || userData[index - 1].isRegistered);
                
                return (
                  <React.Fragment key={user.id}>
                    {/* 註冊用戶分組標題 */}
                    {isFirstRegistered && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ 
                          backgroundColor: '#e3f2fd', 
                          fontWeight: 'bold',
                          color: 'primary.main',
                          borderBottom: '2px solid #1976d2'
                        }}>
                          📧 註冊用戶 ({userData.filter(u => u.isRegistered).length} 人)
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* 訪客用戶分組標題 */}
                    {isFirstGuest && (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ 
                          backgroundColor: '#f3e5f5', 
                          fontWeight: 'bold',
                          color: 'secondary.main',
                          borderBottom: '2px solid #9c27b0'
                        }}>
                          👤 訪客用戶 ({userData.filter(u => !u.isRegistered).length} 人)
                        </TableCell>
                      </TableRow>
                    )}
                    
                    {/* 用戶行 */}
                    <TableRow hover sx={{ 
                      backgroundColor: user.isRegistered ? '#fafafa' : '#ffffff'
                    }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ 
                            fontSize: 16, 
                            color: user.isRegistered ? 'primary.main' : 'text.secondary' 
                          }} />
                          <Box>
                            <Typography variant="body2" sx={{ 
                              fontWeight: user.isRegistered ? 'bold' : 'medium',
                              color: user.isRegistered ? 'primary.main' : 'text.primary'
                            }}>
                              {user.name}
                            </Typography>
                            {!user.isRegistered && (
                              <Typography variant="caption" color="text.secondary">
                                未註冊用戶
                              </Typography>
                            )}
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{
                          fontWeight: user.email ? 'medium' : 'normal',
                          color: user.email ? 'primary.main' : 'text.secondary'
                        }}>
                          {user.email || '無郵箱'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={user.status === 'online' ? '在線' : '離線'}
                          color={user.status === 'online' ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TimeIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {formatLastLogin(user.lastLogin)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.videoCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.checkpointCount}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="強制登出">
                            <span>
                              <IconButton 
                                size="small" 
                                color="warning"
                                onClick={() => handleForceLogout(user)}
                                disabled={user.status === 'offline'}
                              >
                                <LogoutIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="編輯用戶">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={() => handleEditUser(user)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="刪除用戶">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={() => handleDeleteUser(user)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* 編輯用戶對話框 */}
      <Dialog 
        open={editDialogOpen} 
        onClose={() => setEditDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '70vh' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EditIcon />
          編輯用戶資料
        </DialogTitle>
        <DialogContent>
          {selectedUserForEdit && (
            <Box sx={{ mt: 2 }}>
              {/* 基本信息編輯 */}
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <PersonIcon />
                基本信息
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="用戶名稱"
                    value={editForm.name}
                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    margin="normal"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="狀態"
                    value={editForm.status}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value as 'online' | 'offline' })}
                    margin="normal"
                    select
                  >
                    <MenuItem value="online">在線</MenuItem>
                    <MenuItem value="offline">離線</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* VBIDs 數據編輯 */}
              <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <VideoLibraryIcon />
                影片數據 ({userVBids.length} 個影片)
              </Typography>

              {loadingVBids ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                  <Typography sx={{ ml: 2 }}>載入影片數據中...</Typography>
                </Box>
              ) : userVBids.length === 0 ? (
                <Alert severity="info">此用戶沒有任何影片數據</Alert>
              ) : (
                <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                  {userVBids.map((vbid, index) => (
                    <Accordion key={vbid.id} sx={{ mb: 1 }}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                          <BookmarkIcon sx={{ fontSize: 16 }} />
                          <Typography variant="subtitle2" sx={{ flexGrow: 1 }}>
                            {vbid.videoTitle || '無標題'}
                          </Typography>
                          <Chip 
                            label={`${vbid.checkpointCount} 個重點`} 
                            size="small" 
                            color="primary"
                          />
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Grid container spacing={2}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="影片標題"
                              value={vbid.videoTitle}
                              onChange={(e) => handleUpdateVBid(vbid.id, 'videoTitle', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="影片 URL"
                              value={vbid.videoUrl}
                              onChange={(e) => handleUpdateVBid(vbid.id, 'videoUrl', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                              重點記錄 ({vbid.vbids.length} 個)
                            </Typography>
                            {vbid.vbids.map((vbidItem, vbidIndex) => (
                              <Box key={vbidIndex} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Grid container spacing={1}>
                                  <Grid item xs={3}>
                                    <TextField
                                      fullWidth
                                      label="時間 (秒)"
                                      type="number"
                                      value={vbidItem.time}
                                      onChange={(e) => handleUpdateVBid(vbid.id, `vbid_${vbidIndex}_time`, e.target.value)}
                                      size="small"
                                    />
                                  </Grid>
                                  <Grid item xs={9}>
                                    <TextField
                                      fullWidth
                                      label="筆記內容"
                                      value={vbidItem.note}
                                      onChange={(e) => handleUpdateVBid(vbid.id, `vbid_${vbidIndex}_note`, e.target.value)}
                                      size="small"
                                      multiline
                                      rows={2}
                                    />
                                  </Grid>
                                </Grid>
                              </Box>
                            ))}
                          </Grid>
                        </Grid>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setEditDialogOpen(false)}
            startIcon={<CancelIcon />}
          >
            取消
          </Button>
          <Button 
            onClick={handleSaveEdit} 
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!isAdmin}
          >
            儲存變更
          </Button>
        </DialogActions>
      </Dialog>

      {/* 刪除用戶確認對話框 */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>確認刪除用戶</DialogTitle>
        <DialogContent>
          <Typography>
            確定要刪除用戶 "{selectedUserForDelete?.name}" 嗎？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            此操作將刪除該用戶的所有數據，包括：
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            • 用戶檔案 ({selectedUserForDelete?.videoCount || 0} 個影片)
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            • 觀看記錄
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            • 筆記和重點記錄
          </Typography>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 'bold' }}>
            此操作無法復原！
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            取消
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            color="error" 
            variant="contained"
            disabled={!isAdmin}
          >
            確認刪除
          </Button>
        </DialogActions>
      </Dialog>

      {/* 強制登出確認對話框 */}
      <Dialog open={logoutDialogOpen} onClose={() => setLogoutDialogOpen(false)}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LogoutIcon color="warning" />
          確認強制登出用戶
        </DialogTitle>
        <DialogContent>
          <Typography>
            確定要強制登出用戶 "{selectedUserForLogout?.name}" 嗎？
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            此操作將：
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            • 立即將用戶狀態設為離線
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            • 記錄最後登出時間
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2, fontWeight: 'bold' }}>
            用戶將在下次操作時被要求重新登入
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)}>
            取消
          </Button>
          <Button 
            onClick={handleConfirmLogout} 
            variant="contained" 
            color="warning"
            startIcon={<LogoutIcon />}
          >
            確認強制登出
          </Button>
        </DialogActions>
      </Dialog>

      {/* 成功/錯誤訊息 */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Box>
  );
};

export default UserManagementWindow;
